(() => {
  const config = document.getElementById('PWAnalytics');
  if (!config || config.dataset.initialized === 'true') return;
  config.dataset.initialized = 'true';

  const pageTypes = new Set(['index', 'product', 'collection', 'list-collections', 'search', 'cart', 'page', 'policy']);
  const contexts = new Set(['quick_add', 'product', 'product_card', 'storefront']);
  const optionNames = new Set(['color', 'colour', 'print', 'pattern', 'style', 'size']);
  const sortKeys = new Set(['BEST_SELLING', 'COLLECTION_DEFAULT', 'CREATED', 'ID', 'MANUAL', 'PRICE', 'RELEVANCE', 'TITLE']);
  const sortDirections = { 'price-ascending': 'ascending', 'price-descending': 'descending', 'title-ascending': 'ascending', 'title-descending': 'descending', 'created-ascending': 'ascending', 'created-descending': 'descending' };
  const filterKinds = new Set(['availability', 'category', 'price', 'type', 'vendor', 'tag', 'color', 'print', 'pattern', 'style', 'size', 'other_option', 'custom_field']);
  const categories = ['dresses', 'tops', 'matching-sets', 'jeans', 'pants', 'skirts', 'shorts', 'bodysuits', 'jumpsuits', 'knitwear', 'hoodies-sweats', 'jackets-coats', 'activewear', 'swimwear', 'intimates', 'costumes', 'accessories'];
  const trends = ['halloween', 'blackout', 'dark-romance', 'denim-days', 'after-dark', 'soft-romance', 'off-duty', 'everyday-fits', 'animal-instinct', 'festival-energy'];
  const destinations = new Set(['home', 'categories', 'trends', 'bag', 'account', 'search', 'all', 'women', 'men', 'new-in', 'super-deals', 'denim', 'going-out', 'contact', 'terms-of-service', 'privacy-policy', 'shipping-info', 'shipping-delivery', 'returns', 'refunds', ...categories, ...trends, ...categories.flatMap((name) => [`women-${name}`, `men-${name}`])]);
  const placements = new Set(['bottom_navigation', 'header', 'category_directory', 'trend_directory', 'home', 'catalog', 'footer']);
  const sections = new Set(['home_intro', 'category_shortcuts', 'trend_edit', 'super_deals', 'new_in', 'denim', 'product_feed', 'catalog_intro', 'product_details']);
  const eventFields = {
    pw_quick_add_open: [],
    pw_quick_add_close: [],
    pw_option_select: ['option_name', 'option_value', 'option_value_id'],
    pw_size_guide_open: [],
    pw_add_to_bag_error: ['error_code'],
    pw_navigation_select: ['destination', 'placement'],
    pw_filter_apply: ['filter_count', 'filter_types'],
    pw_sort_change: ['sort_key', 'sort_direction'],
    pw_gallery_interaction: ['gallery_action', 'image_index'],
    pw_section_impression: ['section_name']
  };
  const numericId = (value) => typeof value === 'string' && /^\d{1,20}$/.test(value) ? value : undefined;
  const catalogText = (value) => typeof value === 'string' && value.length > 0 && value.length <= 64 && /^[\p{L}\p{N}\s.,+&()/_-]+$/u.test(value) ? value.trim() : undefined;
  const count = (value) => Number.isInteger(value) && value >= 0 && value <= 100 ? value : undefined;
  const validators = {
    option_name: (value) => optionNames.has(value) ? value : undefined,
    option_value: catalogText,
    option_value_id: numericId,
    error_code: (value) => value === 'size_required' ? value : undefined,
    destination: (value) => destinations.has(value) ? value : undefined,
    placement: (value) => placements.has(value) ? value : undefined,
    sort_key: (value) => sortKeys.has(value) ? value : undefined,
    sort_direction: (value) => value === 'ascending' || value === 'descending' ? value : undefined,
    filter_count: count,
    filter_types: (value) => typeof value === 'string' && value.length <= 160 && value.split('|').every((item) => filterKinds.has(item)) ? value : undefined,
    gallery_action: (value) => ['select', 'scroll', 'drag'].includes(value) ? value : undefined,
    image_index: count,
    section_name: (value) => sections.has(value) ? value : undefined
  };
  let consentReady = false;
  let consentFailed = false;
  let consentLoading = false;
  let consentEpoch = 0;
  let impressionObserver;
  const impressions = new Set();
  const observedSections = new WeakSet();
  let sortIntents = new WeakSet();
  const errorVisibility = new WeakMap();

  const allowed = () => {
    if (!consentReady || consentFailed) return false;
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  };
  const contextFor = (node) => {
    if (node?.closest('quick-add-dialog')) return 'quick_add';
    if (node?.closest('product-card')) return 'product_card';
    if (node?.closest('[data-product-grid-content]')) return 'product';
    return 'storefront';
  };
  const productIdFor = (node) => {
    const root = node?.closest('#quick-add-modal-content, [data-product-grid-content], product-card, quick-add-dialog');
    return numericId(root?.dataset.productId) || numericId(root?.querySelector('variant-picker')?.dataset.productId) || numericId(root?.querySelector('[data-pw-product-information]')?.dataset.productId) || (config.dataset.pageType === 'product' ? numericId(config.dataset.productId) : undefined);
  };
  const publish = (name, raw = {}, node) => {
    if (!allowed() || !Object.hasOwn(eventFields, name) || typeof window.Shopify?.analytics?.publish !== 'function') return false;
    const data = { schema_version: 1, page_type: pageTypes.has(config.dataset.pageType) ? config.dataset.pageType : 'other' };
    const context = contextFor(node);
    if (contexts.has(context)) data.context = context;
    const productId = productIdFor(node);
    if (productId) data.product_id = productId;
    for (const key of eventFields[name]) {
      const value = validators[key](raw[key]);
      if (value !== undefined) data[key] = value;
    }
    try {
      const result = window.Shopify.analytics.publish(name, data);
      if (result && typeof result.catch === 'function') result.catch(() => {});
      return result !== false;
    } catch { return false; }
  };
  const destinationFor = (link) => {
    if (link.hasAttribute('data-pw-open-categories')) return 'categories';
    if (link.hasAttribute('data-pw-open-trends')) return 'trends';
    try {
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return undefined;
      const path = url.pathname.replace(/\/$/, '') || '/';
      const fixed = { '/': 'home', '/collections': 'categories', '/cart': 'bag', '/account': 'account', '/search': 'search' };
      if (fixed[path]) return fixed[path];
      const match = path.match(/^\/(?:collections|pages|policies)\/([a-z0-9-]+)$/);
      return match && destinations.has(match[1]) ? match[1] : undefined;
    } catch { return undefined; }
  };
  const placementFor = (node) => {
    if (node.closest('[data-pw-bottom-bar]')) return 'bottom_navigation';
    if (node.closest('[data-pw-dialog="categories"]')) return 'category_directory';
    if (node.closest('[data-pw-dialog="trends"]')) return 'trend_directory';
    if (node.closest('footer')) return 'footer';
    if (node.closest('#header-group')) return 'header';
    return config.dataset.pageType === 'index' ? 'home' : 'catalog';
  };
  const facetRoot = (node) => node.closest('facets-form-component') || node.closest('.shopify-section')?.querySelector('facets-form-component');
  const sortDirectionFor = (root) => {
    const scope = root.closest('.shopify-section') || root;
    const control = scope.querySelector('select[name="sort_by"]:not([disabled]), input[name="sort_by"]:checked:not([disabled])');
    return control && Object.hasOwn(sortDirections, control.value) ? sortDirections[control.value] : undefined;
  };

  document.addEventListener('click', (event) => {
    if (!event.isTrusted || !(event.target instanceof Element) || !allowed()) return;
    const target = event.target;
    const fit = target.closest('[data-pw-fit]');
    if (fit) publish('pw_size_guide_open', {}, fit);
    const link = target.closest('a[href]');
    if (link) {
      const destination = destinationFor(link);
      if (destination) publish('pw_navigation_select', { destination, placement: placementFor(link) }, link);
    }
    if (target.closest('sorting-filter-component')) {
      const root = facetRoot(target);
      if (root) sortIntents.add(root);
    }
  }, true);
  document.addEventListener('change', (event) => {
    if (!event.isTrusted || !(event.target instanceof Element) || !allowed()) return;
    const input = event.target;
    if (input.closest('variant-picker') && input.matches('input[type="radio"][data-option-name]')) {
      const name = input.dataset.optionName?.trim().toLowerCase();
      if (optionNames.has(name)) publish('pw_option_select', { option_name: name, option_value: input.value, option_value_id: input.dataset.optionValueId }, input);
    }
    if (input.getAttribute('name') === 'sort_by') {
      const root = facetRoot(input);
      if (root) sortIntents.add(root);
    }
  }, true);
  for (const action of ['open', 'close']) document.addEventListener(`dialog:${action}`, (event) => {
    if (event.target instanceof Element && event.target.matches('quick-add-dialog')) publish(`pw_quick_add_${action}`, {}, event.target);
  }, true);
  document.addEventListener('slideshow:select', (event) => {
    if (!(event.target instanceof Element) || !event.detail?.userInitiated || !event.target.closest('.product-information__media, media-gallery, product-card')) return;
    publish('pw_gallery_interaction', { gallery_action: event.detail.trigger, image_index: Number.isInteger(event.detail.index) ? event.detail.index + 1 : undefined }, event.target);
  });

  const onRefinement = (event) => {
    if (!(event.target instanceof Element) || !allowed() || !event.promise || typeof event.promise.then !== 'function') return;
    const epoch = consentEpoch;
    const root = facetRoot(event.target) || event.target;
    const sorting = sortIntents.has(root);
    sortIntents.delete(root);
    const details = event.type === 'shopify:search:update' ? event.search : event;
    const types = new Set();
    const filters = Array.isArray(details?.productFilters) ? details.productFilters.slice(0, 100) : [];
    for (const filter of filters) {
      if (!filter || typeof filter !== 'object') continue;
      if (Object.hasOwn(filter, 'available')) types.add('availability');
      if (filter.category) types.add('category');
      if (filter.price) types.add('price');
      if (filter.productType) types.add('type');
      if (filter.productVendor) types.add('vendor');
      if (filter.tag) types.add('tag');
      if (filter.productMetafield || filter.variantMetafield || filter.taxonomyMetafield) types.add('custom_field');
      if (filter.variantOption) {
        const name = typeof filter.variantOption.name === 'string' ? filter.variantOption.name.toLowerCase() : '';
        types.add(name === 'colour' ? 'color' : optionNames.has(name) ? name : 'other_option');
      }
    }
    const data = sorting ? { sort_key: details?.sortKey, sort_direction: sortDirectionFor(root) } : { filter_count: filters.length, filter_types: [...types].sort().join('|') };
    event.promise.then(() => {
      if (epoch === consentEpoch) publish(sorting ? 'pw_sort_change' : 'pw_filter_apply', data, event.target);
    }).catch(() => {});
  };
  document.addEventListener('shopify:collection:update', onRefinement);
  document.addEventListener('shopify:search:update', onRefinement);

  const sectionName = (node) => {
    if (node.matches('.pw-home-intro')) return 'home_intro';
    if (node.matches('.pw-directory')) return 'category_shortcuts';
    if (node.matches('.pw-home-trends')) return 'trend_edit';
    if (node.matches('.pw-catalog-intro')) return 'catalog_intro';
    if (node.matches('[data-pw-product-information]')) return 'product_details';
    if (node.matches('.pw-feed--deals')) return 'super_deals';
    const destination = node.querySelector('.pw-feed__heading a[href]');
    const name = destination ? destinationFor(destination) : undefined;
    return name === 'new-in' ? 'new_in' : name === 'denim' || name === 'denim-days' ? 'denim' : 'product_feed';
  };
  const observeSections = () => {
    if (!allowed() || typeof IntersectionObserver !== 'function') return;
    if (!impressionObserver) impressionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.15 || !allowed()) continue;
        const name = sectionName(entry.target);
        if (!impressions.has(name) && publish('pw_section_impression', { section_name: name }, entry.target)) impressions.add(name);
      }
    }, { threshold: 0.15 });
    document.querySelectorAll('.pw-home-intro, .pw-directory, .pw-home-trends, .pw-feed, .pw-catalog-intro, [data-pw-product-information]').forEach((node) => {
      if (!observedSections.has(node)) { observedSections.add(node); impressionObserver.observe(node); }
    });
  };
  const observeErrors = () => {
    document.querySelectorAll('.pw-option-error').forEach((node) => {
      const visible = !node.hidden;
      if (visible && errorVisibility.get(node) !== true && node.closest('#quick-add-modal-content, [data-product-grid-content]')?.querySelector('[data-pw-awaiting-size="true"]')) publish('pw_add_to_bag_error', { error_code: 'size_required' }, node);
      errorVisibility.set(node, visible);
    });
  };
  const onConsentChange = () => {
    consentEpoch += 1;
    sortIntents = new WeakSet();
    if (allowed()) observeSections();
    else if (impressionObserver) {
      impressionObserver.disconnect();
      impressionObserver = undefined;
      document.querySelectorAll('.pw-home-intro, .pw-directory, .pw-home-trends, .pw-feed, .pw-catalog-intro, [data-pw-product-information]').forEach((node) => observedSections.delete(node));
    }
  };
  const initializeConsent = () => {
    if (consentReady || consentFailed || consentLoading) return;
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') {
      consentReady = true;
      onConsentChange();
      return;
    }
    if (typeof window.Shopify?.loadFeatures !== 'function') return;
    consentLoading = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        consentLoading = false;
        consentFailed = Boolean(error) || typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function';
        consentReady = !consentFailed;
        onConsentChange();
      });
    } catch { consentLoading = false; consentFailed = true; }
  };
  let mutationPending = false;
  new MutationObserver(() => {
    if (mutationPending) return;
    mutationPending = true;
    queueMicrotask(() => { mutationPending = false; observeErrors(); observeSections(); });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
  document.addEventListener('visitorConsentCollected', () => { initializeConsent(); onConsentChange(); });
  window.addEventListener('load', initializeConsent, { once: true });
  initializeConsent();
})();
